import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaDownload, FaArrowRight, FaCheckCircle, FaLock,
  FaAppleAlt, FaDumbbell, FaBriefcase, FaRunning, FaFlask,
  FaEnvelope, FaPhone, FaUser, FaTimes,
} from 'react-icons/fa';
import SEO from '../components/SEO';
import { callFunction } from '../lib/supabase';

// ─── UPDATE THESE WHEN PDFs ARE READY ─────────────────────────────
// Set url to a Google Drive or Supabase Storage shareable link.
// Set hidden: true to temporarily hide a guide until it's ready.
const GUIDES = [
  {
    id: 1,
    title: 'The Indian Weight Loss Diet Guide',
    tagline: 'Lose weight without giving up your favourite Indian foods.',
    overview: [
      'Science-backed nutrition principles adapted for Indian eating habits',
      'Best and worst Indian foods for fat loss — ranked simply',
      'Portion control guide for dal, rice, roti, sabzi & snacks',
      '7-day Indian meal plan to kickstart your fat loss journey',
      'Common mistakes Indians make when dieting — and how to avoid them',
    ],
    icon: FaAppleAlt,
    gradient: 'from-green-500 to-emerald-700',
    accentLight: 'bg-green-50',
    accentText: 'text-green-700',
    url: '/guides/fitnession_weight_loss_guide.pdf',
    coverImage: '/guides/covers/indian-weight-loss-guide.png',
  },
  {
    id: 2,
    title: '7-Day Home Workout Plan for Beginners',
    tagline: 'Build the exercise habit — no gym, no equipment, no excuses.',
    overview: [
      '20-minute daily routines designed for complete beginners',
      'Bodyweight exercises with clear photo instructions',
      'Warm-up and cool-down routines included every day',
      'How to progress week by week without hitting a plateau',
      'Tips to stay consistent when motivation drops',
    ],
    icon: FaDumbbell,
    gradient: 'from-blue-500 to-indigo-700',
    accentLight: 'bg-blue-50',
    accentText: 'text-blue-700',
    url: '/guides/7-day-workout-guide.pdf',
    coverImage: '/guides/covers/7-day-workout-guide.png',
  },
  {
    id: 3,
    hidden: true,
    title: "The Busy Professional's Fitness Playbook",
    tagline: 'Stay fit even with a packed schedule and work-from-home life.',
    overview: [
      'Weekly meal prep system that takes less than 1 hour on Sunday',
      'Desk and office exercises you can do between meetings',
      'How to eat healthy when ordering in or eating out',
      'Quick 10-minute workout routines for busy days',
      'Sleep and stress management tactics for working professionals',
    ],
    icon: FaBriefcase,
    gradient: 'from-purple-500 to-violet-700',
    accentLight: 'bg-purple-50',
    accentText: 'text-purple-700',
    url: '#',
  },
  {
    id: 4,
    hidden: true,
    title: 'Know Your Body Type — And How to Train It',
    tagline: 'Stop following generic advice — train and eat for your unique body.',
    overview: [
      'Understand ectomorph, mesomorph, and endomorph body types',
      'Which training style works best for your body type',
      'Ideal macros and meal structure for each body type',
      'Why the same diet works for your friend but not you',
      'How to identify your dominant body type with a simple checklist',
    ],
    icon: FaRunning,
    gradient: 'from-orange-500 to-amber-700',
    accentLight: 'bg-orange-50',
    accentText: 'text-orange-700',
    url: '#',
  },
  {
    id: 5,
    hidden: true,
    title: 'The Truth About Indian Supplements',
    tagline: "Save money and get results — know what actually works for Indian bodies.",
    overview: [
      "Which supplements are worth buying and which are pure marketing",
      'Protein powders — what to look for on the label as an Indian',
      'Natural Indian food alternatives to expensive supplements',
      'Supplements that are genuinely useful for vegetarians and vegans',
      'Safe dosing guidelines and what to avoid combining',
    ],
    icon: FaFlask,
    gradient: 'from-rose-500 to-pink-700',
    accentLight: 'bg-rose-50',
    accentText: 'text-rose-700',
    url: '#',
  },
];

// ─── CSS Guide Cover ───────────────────────────────────────────────
function GuideCover({ guide, size = 'lg' }) {
  const isLg = size === 'lg';

  // Use real cover image if available
  if (guide.coverImage) {
    return (
      <div className="relative w-full rounded-2xl overflow-hidden shadow-xl select-none" style={{ aspectRatio: '3/4' }}>
        <img
          src={guide.coverImage}
          alt={guide.title}
          className="w-full h-full object-cover"
        />
        <span className="absolute top-2 left-2 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wide shadow">
          Free
        </span>
      </div>
    );
  }

  // Fallback CSS cover
  return (
    <div className={`relative w-full bg-gradient-to-br ${guide.gradient} rounded-2xl overflow-hidden flex flex-col select-none`}
      style={{ aspectRatio: '3/4' }}>
      {/* Background circles */}
      <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full" />
      <div className="absolute -bottom-10 -left-10 w-44 h-44 bg-white/10 rounded-full" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-56 h-56 bg-white/5 rounded-full" />

      <div className="relative z-10 flex flex-col h-full p-5">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-3">
          <span className={`text-white/80 font-black italic ${isLg ? 'text-sm' : 'text-xs'}`}>
            Fitnession
          </span>
          <span className="bg-white/20 text-white text-xs font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide">
            Free
          </span>
        </div>

        {/* Icon */}
        <div className="flex-1 flex items-center justify-center">
          <div className={`bg-white/20 rounded-3xl flex items-center justify-center backdrop-blur-sm shadow-xl
            ${isLg ? 'w-24 h-24' : 'w-14 h-14'}`}>
            <guide.icon className={`text-white drop-shadow ${isLg ? 'text-5xl' : 'text-2xl'}`} />
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-4">
          <div className="w-8 h-0.5 bg-white/40 mb-3 rounded-full" />
          <h3 className={`text-white font-bold leading-snug mb-2 ${isLg ? 'text-base' : 'text-xs'}`}>
            {guide.title}
          </h3>
        </div>
      </div>
    </div>
  );
}

// ─── Modal ─────────────────────────────────────────────────────────
function GuideModal({ guide, onClose, unlockedGuides, onUnlock, savedForm, onFormChange }) {
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const isUnlocked = unlockedGuides.has(guide.id);

  // Close on Escape
  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose]);

  const validate = () => {
    const e = {};
    if (!savedForm.name.trim()) e.name = 'Name is required';
    if (!savedForm.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(savedForm.email)) e.email = 'Enter a valid email';
    if (!savedForm.phone.trim()) e.phone = 'Phone is required';
    else if (!/^[6-9]\d{9}$/.test(savedForm.phone.replace(/\s/g, ''))) e.phone = 'Enter a valid 10-digit number';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setLoading(true);
    setServerError('');
    try {
      await callFunction('save-lead', {
        name: savedForm.name.trim(),
        email: savedForm.email.trim(),
        phone: savedForm.phone.trim(),
        guideTitle: guide.title,
        guideUrl: guide.url,
      });
      onUnlock(guide.id);
    } catch {
      setServerError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = (key) =>
    `w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors text-gray-900 text-sm ${
      errors[key] ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'
    }`;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Close btn */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 bg-gray-100 hover:bg-gray-200 text-gray-600 p-2 rounded-full transition-colors"
        >
          <FaTimes />
        </button>

        <div className="grid grid-cols-1 sm:grid-cols-5 gap-0">
          {/* Left — cover */}
          <div className="sm:col-span-2 p-6 pb-0 sm:pb-6">
            <GuideCover guide={guide} size="lg" />
          </div>

          {/* Right — details + form */}
          <div className="sm:col-span-3 p-6">
            <span className={`inline-block text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-3 ${guide.accentLight} ${guide.accentText}`}>
              Free Guide
            </span>
            <h2 className="text-xl font-extrabold text-gray-900 leading-snug mb-1">{guide.title}</h2>
            <p className="text-gray-500 text-sm mb-4">{guide.tagline}</p>

            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">What's inside</p>
            <ul className="space-y-1.5 mb-6">
              {guide.overview.map((point, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                  <FaCheckCircle className={`flex-shrink-0 mt-0.5 ${guide.accentText}`} size={13} />
                  {point}
                </li>
              ))}
            </ul>

            {isUnlocked ? (
              /* ── Already unlocked ── */
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-green-600 text-sm font-semibold bg-green-50 rounded-xl px-4 py-3">
                  <FaCheckCircle /> Guide unlocked!
                </div>
                <a
                  href={guide.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-bold text-sm transition-all ${
                    guide.url === '#'
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg hover:shadow-green-500/30'
                  }`}
                  onClick={e => guide.url === '#' && e.preventDefault()}
                >
                  <FaDownload /> Download PDF
                </a>
                {guide.url === '#' && (
                  <p className="text-center text-xs text-gray-400">PDF coming soon — we will notify you by email.</p>
                )}
              </div>
            ) : (
              /* ── Form ── */
              <form onSubmit={handleSubmit} noValidate className="space-y-3">
                <p className="text-xs font-semibold text-gray-500 mb-1">Enter your details to download</p>

                {[
                  { key: 'name', label: 'Full Name', type: 'text', placeholder: 'Rahul Sharma', icon: <FaUser size={13} /> },
                  { key: 'email', label: 'Email', type: 'email', placeholder: 'rahul@example.com', icon: <FaEnvelope size={13} /> },
                  { key: 'phone', label: 'Mobile Number', type: 'tel', placeholder: '9876543210', icon: <FaPhone size={13} /> },
                ].map(({ key, label, type, placeholder, icon }) => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                        {icon}
                      </div>
                      <input
                        type={type}
                        value={savedForm[key]}
                        onChange={e => onFormChange(key, e.target.value)}
                        placeholder={placeholder}
                        className={inputCls(key)}
                      />
                    </div>
                    {errors[key] && <p className="text-red-500 text-xs mt-1">{errors[key]}</p>}
                  </div>
                ))}

                {serverError && (
                  <p className="text-red-500 text-xs text-center bg-red-50 rounded-lg p-2">{serverError}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-3.5 rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 text-sm disabled:opacity-70"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Unlocking...
                    </>
                  ) : (
                    <><FaDownload /> Get Free Guide</>
                  )}
                </button>

                <p className="text-center text-xs text-gray-400 flex items-center justify-center gap-1">
                  <FaLock size={10} className="text-gray-300" /> No spam. Unsubscribe anytime.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────
export default function FreeResources() {
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [unlockedGuides, setUnlockedGuides] = useState(new Set());
  const [form, setForm] = useState({ name: '', email: '', phone: '' });

  const handleUnlock = (id) => {
    setUnlockedGuides(prev => new Set([...prev, id]));
  };

  const handleFormChange = (key, value) => {
    setForm(f => ({ ...f, [key]: value }));
  };

  return (
    <>
      <SEO
        title="Free Fitness Guides — Download Now | Fitnession"
        description="Download free expert fitness guides covering Indian weight loss, home workouts, body types, supplements, and more. No cost, instant access."
      />

      <div className="min-h-screen bg-white">

        {/* ── Hero ── */}
        <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-green-900 text-white pt-16 pb-28 px-4 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-96 h-96 bg-green-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-3xl mx-auto text-center">
            <span className="inline-block bg-green-500/20 border border-green-500/30 text-green-400 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
              Free Download — No Credit Card Needed
            </span>
            <h1 className="text-4xl sm:text-5xl font-extrabold mb-5 leading-tight">
              Expert Fitness Guides —{' '}
              <span className="bg-gradient-to-r from-green-400 to-teal-400 bg-clip-text text-transparent">
                Completely Free
              </span>
            </h1>
            <p className="text-gray-300 text-lg max-w-xl mx-auto mb-8">
              Guides crafted by Fitnession coaches. Click any guide below to read what's inside and download it free.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-400">
              {['No spam ever', 'Instant access', 'PDF format', 'Expert-crafted'].map(t => (
                <span key={t} className="flex items-center gap-1.5">
                  <FaCheckCircle className="text-green-500" /> {t}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ── Guide grid ── */}
        <section className="max-w-6xl mx-auto px-4 -mt-12 relative z-10 pb-16">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {GUIDES.filter(g => !g.hidden).map(guide => {
              const unlocked = unlockedGuides.has(guide.id);
              return (
                <button
                  key={guide.id}
                  onClick={() => setSelectedGuide(guide)}
                  className="group text-left focus:outline-none"
                >
                  {/* Cover with hover effect */}
                  <div className="relative mb-3 rounded-2xl overflow-hidden shadow-xl group-hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-1">
                    <GuideCover guide={guide} size="sm" />
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                      <span className="bg-white text-gray-900 text-xs font-bold px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg">
                        {unlocked ? 'Download' : 'View Guide'}
                      </span>
                    </div>
                    {unlocked && (
                      <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1 shadow-lg">
                        <FaCheckCircle size={12} />
                      </div>
                    )}
                  </div>
                  <p className="text-gray-800 font-semibold text-xs leading-snug line-clamp-2 group-hover:text-green-700 transition-colors">
                    {guide.title}
                  </p>
                </button>
              );
            })}
          </div>
        </section>

        {/* ── Social proof ── */}
        <section className="border-t border-gray-100 bg-gray-50 py-14 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-gray-400 text-sm font-medium uppercase tracking-wide mb-8">
              Trusted by 1,000+ members across India
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                { quote: 'The Indian diet guide changed the way I eat. Lost 8 kg in 2 months!', name: 'Priya M., Pune' },
                { quote: 'The home workout plan was exactly what I needed — no gym, no excuse.', name: 'Arjun S., Bangalore' },
                { quote: "The supplement guide saved me money. Stopped buying things that don't work.", name: 'Meera R., Mumbai' },
              ].map(t => (
                <div key={t.name} className="bg-white border border-gray-100 rounded-2xl p-5 text-left shadow-sm">
                  <p className="text-gray-600 text-sm leading-relaxed mb-3">"{t.quote}"</p>
                  <p className="text-gray-400 text-xs font-medium">— {t.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="py-14 px-4 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Want a personalised plan?</h2>
          <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
            Guides are a great start. A custom plan built for your body gets you there faster.
          </p>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold px-8 py-3.5 rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg"
          >
            View Coaching Programs <FaArrowRight />
          </Link>
        </section>

      </div>

      {/* ── Modal ── */}
      {selectedGuide && (
        <GuideModal
          guide={selectedGuide}
          onClose={() => setSelectedGuide(null)}
          unlockedGuides={unlockedGuides}
          onUnlock={handleUnlock}
          savedForm={form}
          onFormChange={handleFormChange}
        />
      )}
    </>
  );
}
